import React, { useCallback, useEffect, useMemo, useState } from "react";
import NavBrandGlyph from "./NavBrandGlyph";
import PathwaySearchField from "../../../_UI/Components/Inputs/PathwaySearchField";
import { isActivePath } from "./isActivePath";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router";
import styled, { css } from "styled-components";
import PathwayNavItem from "../../../_UI/Components/PathwayNavItem";
import VipBadge from "../../../_UI/Components/VipBadge";
import PathwayBlockButton from "../../../_UI/Components/PathwayBlockButton";
import { sortNumValuesByField } from "../../../_services/sorting-helper";
import useUserAuthentication from "../../../_hooks/useUserAuthentication";
import useHelpCenter from "../../../_hooks/useHelpCenter";
import { useUserDropdownUtils } from "../UserDropDownUtils";
import { selectOrganization } from "../../../user/services/store/user.actions";
import { adminAreaActions } from "../../../admin/services/store/adminarea.actions";
import { ReportBugModal } from "../../../admin/components/organizationAdmin/ReportBugModal";
import { OrganizationType } from "../../../_statics/OrganizationType";
import EnvironmentBadge from "./EnvironmentBadge";
import groupNavigationItems from "./groupNavigationItems";
import { mobile } from "../../../design-system/tokens/breakpoints";
// The "p" mark, not the full lockup — public/logo.png is 1371x355 and would be
// illegible at 24x26, and the wordmark next to it would be duplicated.
import brandMark from "../../../_assets/img/Logo.svg";

const Rail = styled.nav`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: max-content minmax(0, 1fr) max-content;
    width: var(--pw-sidebar-width);
    height: 100vh;
    position: sticky;
    top: 0;
    z-index: var(--pw-z-rail);
    padding: 14px 12px 12px;
    background: var(--surface-rail);
    border-right: 1px solid var(--border-hairline);

    ${mobile} {
        position: fixed;
        top: 0;
        left: 0;
        height: 100dvh;
        transform: translateX(${(p) => (p.$mobileOpen ? "0" : "-100%")});
        transition: transform 0.25s ease;
        box-shadow: ${(p) => (p.$mobileOpen ? "var(--pw-shadow-lg)" : "none")};
    }
`;

const MobileScrim = styled.div`
    display: none;

    ${mobile} {
        ${(p) =>
            p.$visible &&
            css`
                display: block;
                position: fixed;
                inset: 0;
                z-index: calc(var(--pw-z-rail) - 1);
                background: var(--pw-overlay-scrim);
            `}
    }
`;

const Brand = styled(Link)`
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 2px 11px 12px;
    text-decoration: none;

    &:hover {
        opacity: 0.8;
    }
`;

const BrandMark = styled.img`
    width: 24px;
    height: 26px;
    object-fit: contain;
    flex: none;
`;

const BrandName = styled.span`
    font-family: var(--font-display);
    font-size: var(--pw-text-md);
    font-weight: var(--pw-weight-bold);
    color: var(--text-heading);
`;

const OrgButton = styled.button`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    padding: 8px 10px;
    border: 1px solid var(--border-hairline);
    border-radius: var(--pw-radius-lg);
    background: var(--surface-card);
    box-shadow: var(--pw-shadow-xs);
    font-family: inherit;
    text-align: left;
    cursor: pointer;
    transition: background var(--pw-transition-fast) var(--pw-ease);

    &:hover {
        background: var(--pw-neutral-75);
    }
`;

const OrgMark = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex: none;
    border-radius: 7px;
    background: var(--pw-neutral-100);
    color: var(--text-muted);
    font-size: var(--pw-text-base);
`;

const Caret = styled.i`
    flex: none;
    color: var(--text-subtle);
    font-size: var(--pw-text-sm);
`;

const OrgName = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--pw-text-hint);
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
`;



const AddAction = styled.div`
    margin-top: var(--gap-m);
`;

const NavArea = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-content: start;
    min-height: 0;
    margin-top: 16px;
    overflow-y: auto;
`;

const GroupLabel = styled.div`
    padding: 6px 10px 3px;
    font-size: var(--pw-text-xs);
    font-weight: var(--pw-weight-semibold);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-subtle);
`;

const Footer = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    row-gap: 2px;
    padding-top: 14px;
`;

const HelpNotReadyHint = styled.p`
    margin: 2px 8px 0;
    padding: 6px 10px;
    border-radius: var(--pw-radius-sm);
    background: var(--pw-neutral-75);
    color: var(--pw-neutral-500);
    font-size: var(--pw-text-2xs);
    line-height: 1.4;
`;

const UserButton = styled(OrgButton)`
    box-shadow: none;
    border-color: transparent;
    border-radius: var(--pw-radius-md);
    background: ${(props) => (props.$active ? "var(--pw-neutral-75)" : "var(--surface-card)")};
`;

const Avatar = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    flex: none;
    border-radius: 50%;
    background: var(--pw-primary);
    color: var(--text-on-brand);
    font-size: var(--pw-text-sm);
    font-weight: var(--pw-weight-bold);
`;

const UserLines = styled.span`
    flex: 1;
    min-width: 0;
    line-height: 1.2;
`;

const UserName = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--pw-text-base);
    font-weight: var(--pw-weight-semibold);
    color: var(--text-heading);
`;

const UserOrg = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--pw-text-xs);
    color: var(--text-muted);
`;

const Menu = styled.div`
    position: absolute;
    z-index: var(--pw-z-rail-menu);
    width: 320px;
    max-height: 60vh;
    overflow-y: auto;
    padding: 6px;
    background: var(--surface-card);
    border: 1px solid var(--border-hairline);
    border-radius: var(--pw-radius-xl);
    box-shadow: var(--pw-shadow-lg);
`;

const OrgMenu = styled(Menu)`
    left: 12px;
    top: 104px; /* brand area (~88px) + org-switcher button (~16px top gap) */

    ${mobile} {
        left: 12px;
        right: 12px;
        width: auto;
    }
`;

const UserMenu = styled(Menu)`
    left: calc(var(--pw-sidebar-width) + 12px);
    bottom: 14px;
    width: 326px;

    ${mobile} {
        left: 12px;
        right: 12px;
        width: auto;
        bottom: 64px;
    }
`;

const MenuHeading = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 11px 6px;
    font-size: var(--pw-text-xs);
    font-weight: var(--pw-weight-semibold);
    letter-spacing: var(--pw-tracking-caps);
    text-transform: uppercase;
    color: var(--text-subtle);
`;

const MenuUser = styled.div`
    padding: 11px 12px 13px;
    line-height: 1.4;
`;

const MenuUserName = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--pw-text-md);
    font-weight: var(--pw-weight-bold);
    color: var(--text-heading);
`;

const MenuUserSubline = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: var(--pw-text-md);
    font-weight: var(--pw-weight-light);
    color: var(--text-muted);
`;

const MenuCount = styled.span`
    font-size: var(--pw-text-xs);
    font-weight: var(--pw-weight-light);
    text-transform: none;
    letter-spacing: normal;
    color: var(--text-muted);
`;

// Renders inside the rail so it shares the rail's stacking context and sits
// below the menus. As a sibling it painted over them, swallowing hover and
// clicks on the very menu it was meant to dismiss.
const Scrim = styled.div`
    position: fixed;
    inset: 0;
    z-index: var(--pw-z-rail-scrim);
`;

const initials = (name) =>
    (name || "")
        .split(/[\s,]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();

export const NavRail = ({ initialMenu, mobileOpen = false, onClose }) => {
    const navigation = useSelector((state) => state.navigation);
    const adminArea = useSelector((state) => state.adminArea);
    const authentication = useSelector((state) => state.authentication);
    const switchingOrganization = useSelector((state) => state.user.switchOrganizationLoading);
    const sidebarBadges = useSelector((state) => state.commons.sidebarBadges);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [orgOpen, setOrgOpen] = useState(initialMenu === "org");
    const [userOpen, setUserOpen] = useState(initialMenu === "user");
    const [helpNotReady, setHelpNotReady] = useState(false);

    const { userDropdownItems, userName, userSubline } = useUserDropdownUtils();
    const { isSuperUser, isTaxConsultant, selectedOrganization } = useUserAuthentication();
    const { openHelpCenter } = useHelpCenter();

    const groups = useMemo(() => groupNavigationItems(navigation.items), [navigation.items]);

    const activeUrl = useMemo(() => {
        // Most specific (longest URL) match wins. A plain prefix match would pick /integrations/shopify
        // over /integrations/shopify/payments when both appear in the nav, because Array.find() returns
        // the first match it encounters regardless of specificity.
        const match = (navigation.items || []).reduce((best, item) => {
            if (!isActivePath(location.pathname, item.url)) return best;
            if (!best || item.url.length > best.url.length) return item;
            return best;
        }, null);
        return match ? match.url : location.pathname === "/" ? "/" : null;
    }, [location.pathname, navigation.items]);

    // Account pages (profile, billing, admin, API connections, …) have no matching entry in the main
    // rail, so the account button marks itself active instead of leaving every entry unmarked.
    const isAccountActive = useMemo(
        () => userDropdownItems.some((item) => isActivePath(location.pathname, item.url)),
        [location.pathname, userDropdownItems]
    );

    const attentionIntegrations = useMemo(
        () => new Set(sidebarBadges?.integrationsNeedingAttention || []),
        [sidebarBadges]
    );

    const organizations = useMemo(
        () => authentication?.user?.availableOrganizations || [],
        [authentication?.user?.availableOrganizations]
    );
    const organizationUUID = selectedOrganization?.externalUUID;
    const organizationName = selectedOrganization?.name || "";

    const taxAgencyOrg = useMemo(
        () => organizations.find((organization) => organization.taxAgency) || null,
        [organizations]
    );
    const showBackToKanzlei =
        isTaxConsultant && taxAgencyOrg && selectedOrganization?.organizationType !== OrganizationType.TAX_AGENCY;

    const [orgQuery, setOrgQuery] = useState("");

    // The account you are in belongs at the top — it is the one people look for to confirm where they are.
    // The rest keeps the order the backend sent.
    const visibleOrganizations = useMemo(() => {
        const query = orgQuery.trim().toLowerCase();
        const matching = query
            ? organizations.filter((organization) => (organization.name || "").toLowerCase().includes(query))
            : organizations;

        return [...matching].sort((a, b) => {
            if (a.externalUUID === organizationUUID) return -1;
            if (b.externalUUID === organizationUUID) return 1;
            return 0;
        });
    }, [organizations, orgQuery, organizationUUID]);

    const closeMenus = useCallback(() => {
        setOrgOpen(false);
        setUserOpen(false);
        setOrgQuery("");
        onClose?.();
    }, [onClose]);

    // A menu that can only be dismissed by clicking the scrim is a trap for anyone on a keyboard, and
    // Escape is where every user reaches first.
    useEffect(() => {
        if (!orgOpen && !userOpen) return undefined;

        const onKeyDown = (event) => {
            if (event.key === "Escape") closeMenus();
        };

        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [orgOpen, userOpen, closeMenus]);

    const go = (url) => {
        closeMenus();
        navigate(url);
    };

    return (
        <>
            <MobileScrim $visible={mobileOpen} onClick={onClose} />
            <Rail aria-label="Hauptnavigation" data-cy="nav-rail" $mobileOpen={mobileOpen}>
                {(orgOpen || userOpen) && <Scrim onClick={closeMenus} data-cy="rail-scrim" />}

                <div>
                    <Brand to="/">
                        <BrandMark src={brandMark} alt="pathway solutions" />
                        <BrandName>pathway</BrandName>
                        <EnvironmentBadge />
                    </Brand>

                    <OrgButton
                        type="button"
                        onClick={() => {
                            setUserOpen(false);
                            setOrgOpen(!orgOpen);
                        }}
                        aria-expanded={orgOpen}
                        data-cy="rail-organization-switcher"
                    >
                        <OrgMark>
                            <i className="fa fa-building" aria-hidden="true" />
                        </OrgMark>
                        <OrgName>{organizationName}</OrgName>
                        <VipBadge
                            hasActiveVipSupport={selectedOrganization?.hasActiveVipSupport}
                            isSuperUser={isSuperUser}
                            size="sm"
                        />
                        <Caret className="fa fa-sort" aria-hidden="true" />
                    </OrgButton>

                    <AddAction>
                        <PathwayBlockButton
                            icon={<i className="fa fa-plus" aria-hidden="true" />}
                            active={isActivePath(location.pathname, "/addintegrations")}
                            onClick={() => go("/addintegrations")}
                            dataCy="rail-add-integration"
                        >
                            Integration hinzufügen
                        </PathwayBlockButton>
                    </AddAction>
                </div>

                <NavArea>
                    {showBackToKanzlei && (
                        <PathwayNavItem
                            label="Zurück zur Kanzlei"
                            icon={<i className="fa fa-arrow-left" aria-hidden="true" />}
                            onClick={() => {
                                if (switchingOrganization) return;
                                dispatch(selectOrganization(taxAgencyOrg.externalUUID, true));
                            }}
                            dataCy="back-to-kanzlei-button"
                        />
                    )}

                    {groups.map((group, groupIndex) => (
                        <div key={group.label || groupIndex}>
                            {group.label && <GroupLabel>{group.label}</GroupLabel>}
                            {group.items.map((item) => (
                                <PathwayNavItem
                                    key={item.url || item.name}
                                    label={item.name}
                                    icon={<NavBrandGlyph url={item.url} icon={item.icon} />}
                                    active={item.url === activeUrl}
                                    attention={!!item.identifier && attentionIntegrations.has(item.identifier)}
                                    count={item.url === "/system/buchungsstapel" ? sidebarBadges?.accountingTasksCreatedThisMonth : undefined}
                                    to={item.url}
                                    onClick={closeMenus}
                                    dataCy={item.attributes?.dataCy}
                                />
                            ))}
                        </div>
                    ))}
                </NavArea>

                <Footer>
                    <PathwayNavItem
                        label="Hilfe"
                        icon={<i className="fa fa-question-circle" aria-hidden="true" />}
                        onClick={() => {
                            closeMenus();
                            const opened = openHelpCenter();
                            if (!opened) {
                                setHelpNotReady(true);
                                setTimeout(() => setHelpNotReady(false), 3000);
                            }
                        }}
                        dataCy="rail-help"
                    />
                    {helpNotReady && (
                        <HelpNotReadyHint>
                            Das Hilfe-Center wird noch geladen – bitte kurz warten und erneut versuchen.
                        </HelpNotReadyHint>
                    )}

                    {isSuperUser && organizationUUID && (
                        <PathwayNavItem
                            label="Bug melden"
                            icon={<i className="fa fa-bug" aria-hidden="true" />}
                            tone="danger"
                            onClick={() => dispatch(adminAreaActions.toggleBugReportModal())}
                            dataCy="rail-report-bug"
                        />
                    )}

                    <UserButton
                        type="button"
                        $active={isAccountActive}
                        onClick={() => {
                            setOrgOpen(false);
                            setUserOpen(!userOpen);
                        }}
                        aria-expanded={userOpen}
                        aria-current={isAccountActive ? "page" : undefined}
                        data-cy="rail-user-menu-toggle"
                    >
                        <Avatar aria-hidden="true">{initials(userName)}</Avatar>
                        <UserLines>
                            <UserName>{userName}</UserName>
                            <UserOrg>{organizationName}</UserOrg>
                        </UserLines>
                        <Caret className="fa fa-sort" aria-hidden="true" />
                    </UserButton>
                </Footer>

                {orgOpen && (
                    <OrgMenu data-cy="rail-organization-menu">
                        <MenuHeading>
                            Organisation wechseln
                            <MenuCount>{organizations.length} Accounts</MenuCount>
                        </MenuHeading>
                        <OrgSearch>
                            <PathwaySearchField
                                value={orgQuery}
                                onChange={setOrgQuery}
                                placeholder="Organisation suchen…"
                                ariaLabel="Organisation suchen"
                                width="100%"
                                dataCy="rail-organization-search"
                            />
                        </OrgSearch>
                        <OrgList>
                        {visibleOrganizations.map((organization) => (
                            <PathwayNavItem
                                key={organization.externalUUID}
                                label={organization.name}
                                icon={<i className="fa fa-building" aria-hidden="true" />}
                                badge={
                                    <VipBadge
                                        hasActiveVipSupport={organization.hasActiveVipSupport}
                                        isSuperUser={isSuperUser}
                                        size="sm"
                                    />
                                }
                                active={organization.externalUUID === organizationUUID}
                                checked={organization.externalUUID === organizationUUID}
                                iconBadge
                                onClick={() => {
                                    closeMenus();
                                    if (switchingOrganization) return;
                                    dispatch(selectOrganization(organization.externalUUID, true));
                                }}
                                dataCy={"rail-organization-" + organization.externalUUID}
                            />
                        ))}
                        {visibleOrganizations.length === 0 && (
                            <MenuEmpty data-cy="rail-organization-empty">Keine Organisation gefunden</MenuEmpty>
                        )}
                        </OrgList>
                    </OrgMenu>
                )}

                {userOpen && (
                    <UserMenu data-cy="rail-user-menu">
                        <MenuUser>
                            <MenuUserName>{userName}</MenuUserName>
                            <MenuUserSubline>{userSubline}</MenuUserSubline>
                        </MenuUser>
                        {sortNumValuesByField(userDropdownItems, "index")
                            .filter((item) => !item.hidden)
                            .map((item) => (
                                <PathwayNavItem
                                    key={item.index}
                                    label={item.name}
                                    icon={<NavBrandGlyph url={item.url} icon={item.icon} />}
                                    count={item.count}
                                    to={item.url}
                                    onClick={closeMenus}
                                    dataCy={item.dataCy}
                                />
                            ))}
                    </UserMenu>
                )}
            </Rail>

            {isSuperUser && organizationUUID && (
                <ReportBugModal
                    isModalOpen={!!adminArea?.bugReportModalOpen}
                    bugReportLoading={adminArea?.bugReportLoading}
                    bugReportResult={adminArea?.bugReportResult}
                    bugReportError={adminArea?.bugReportError}
                    organizationUUID={organizationUUID}
                    dispatch={dispatch}
                    milestones={adminArea?.milestones}
                    milestonesLoading={adminArea?.milestonesLoading}
                    projectLabels={adminArea?.projectLabels}
                    projectLabelsLoading={adminArea?.projectLabelsLoading}
                />
            )}
        </>
    );
};

export default NavRail;


const OrgSearch = styled.div`
    padding: 0 0 8px;
`;

// Only the list scrolls, so the search field stays reachable while filtering a long account list.
const OrgList = styled.div`
    max-height: 300px;
    overflow-y: auto;
`;

const MenuEmpty = styled.p`
    margin: 0;
    padding: 10px 12px;
    font-size: var(--pw-text-sm);
    color: var(--text-muted);
`;
